/**
 * MedicalHistoryLog Component
 *
 * Main container for displaying a patient's unified medical history log.
 * Supports two views: compact grid (default) and card-based tabs.
 *
 * Features:
 * - Header with stats (documents, entries, pending merges, unlinked meds)
 * - View toggle: Grid (compact spreadsheet) | Cards (tabbed layout)
 * - Merge suggestion banner with batch/triage
 * - Add entry, edit, verify, delete actions
 */

import { useState } from 'react';
import {
  Stethoscope,
  Pill,
  Syringe,
  FileText,
  GitMerge,
  AlertTriangle,
  Link2,
  ChevronDown,
  ChevronRight,
  Plus,
  RefreshCw,
  LayoutGrid,
  LayoutList,
  Strikethrough,
} from 'lucide-react';
import { HistoryEntryCard, type HistoryEntry, type HistoryEntryType } from './HistoryEntryCard';
import { MergeSuggestionBanner } from './MergeSuggestionBanner';
import { CompactHistoryGrid } from './CompactHistoryGrid';
import type {
  HistoryLog,
  HistoryDiagnosis,
  HistoryMedication,
  HistoryProcedure,
  MergeSuggestion,
  MedDxLink,
  DiagnosisStatus,
  MedicationStatus,
  ProcedureStatus,
} from '../../types';

interface MedicalHistoryLogProps {
  historyLog: HistoryLog;
  diagnoses: HistoryDiagnosis[];
  medications: HistoryMedication[];
  procedures: HistoryProcedure[];
  mergeSuggestions?: MergeSuggestion[];
  medDxLinks?: MedDxLink[];
  onRefresh?: () => void;
  onAddEntry?: (entryType: HistoryEntryType) => void;
  onEditEntry?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onVerifyEntry?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onDeleteEntry?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onUpdateNotes?: (entry: HistoryEntry, entryType: HistoryEntryType, notes: string) => void;
  onStrikeOut?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onUndoStrikeOut?: (entry: HistoryEntry, entryType: HistoryEntryType) => void;
  onPageClick?: (documentId: string, page: number) => void;
  onAcceptMerge?: (suggestion: MergeSuggestion) => Promise<void>;
  onRejectMerge?: (suggestion: MergeSuggestion) => Promise<void>;
  onBatchAcceptMerge?: () => Promise<void>;
  onBatchRejectMerge?: () => Promise<void>;
  onTriageComplete?: () => void;
  onEditDiagnosis?: (id: string, field: string, value: any) => void;
  onEditMedication?: (id: string, field: string, value: any) => void;
  onEditProcedure?: (id: string, field: string, value: any) => void;
}

type TabType = 'diagnoses' | 'medications' | 'procedures';
type ViewMode = 'grid' | 'cards';

const TABS: Array<{ id: TabType; label: string; icon: typeof Stethoscope }> = [
  { id: 'diagnoses', label: 'Diagnoses', icon: Stethoscope },
  { id: 'medications', label: 'Medications', icon: Pill },
  { id: 'procedures', label: 'Procedures', icon: Syringe },
];

// Status grouping sections
const DIAGNOSIS_SECTIONS: Array<{
  status: DiagnosisStatus;
  label: string;
  color: string;
  bgColor: string;
}> = [
  { status: 'active', label: 'Active', color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-50 dark:bg-green-900/20' },
  { status: 'resolved', label: 'Resolved', color: 'text-gray-700 dark:text-gray-300', bgColor: 'bg-gray-50 dark:bg-gray-900/40' },
  { status: 'historical', label: 'Historical', color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
  { status: 'uncertain', label: 'Uncertain', color: 'text-amber-700 dark:text-amber-300', bgColor: 'bg-amber-50 dark:bg-amber-900/20' },
];

const MEDICATION_SECTIONS: Array<{
  status: MedicationStatus;
  label: string;
  color: string;
  bgColor: string;
}> = [
  { status: 'current', label: 'Current', color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-50 dark:bg-green-900/20' },
  { status: 'discontinued', label: 'Discontinued', color: 'text-gray-700 dark:text-gray-300', bgColor: 'bg-gray-50 dark:bg-gray-900/40' },
  { status: 'historical', label: 'Historical', color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
  { status: 'prn', label: 'PRN (As Needed)', color: 'text-amber-700 dark:text-amber-300', bgColor: 'bg-amber-50 dark:bg-amber-900/20' },
  { status: 'uncertain', label: 'Uncertain', color: 'text-orange-700 dark:text-orange-300', bgColor: 'bg-orange-50 dark:bg-orange-900/20' },
];

const PROCEDURE_SECTIONS: Array<{
  status: ProcedureStatus;
  label: string;
  color: string;
  bgColor: string;
}> = [
  { status: 'completed', label: 'Completed', color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-50 dark:bg-green-900/20' },
  { status: 'planned', label: 'Planned', color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
  { status: 'cancelled', label: 'Cancelled', color: 'text-gray-700 dark:text-gray-300', bgColor: 'bg-gray-50 dark:bg-gray-900/40' },
  { status: 'uncertain', label: 'Uncertain', color: 'text-amber-700 dark:text-amber-300', bgColor: 'bg-amber-50 dark:bg-amber-900/20' },
];

interface CollapsibleSectionProps {
  title: string;
  count: number;
  color: string;
  bgColor: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({
  title,
  count,
  color,
  bgColor,
  defaultExpanded = true,
  children,
}: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (count === 0) return null;

  return (
    <div className="mb-4 last:mb-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg ${bgColor} hover:opacity-80 transition-opacity`}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown size={16} className={color} />
          ) : (
            <ChevronRight size={16} className={color} />
          )}
          <span className={`text-sm font-medium ${color}`}>{title}</span>
        </div>
        <span className={`text-xs font-medium ${color} bg-white/50 dark:bg-white/10 px-2 py-0.5 rounded-full`}>
          {count}
        </span>
      </button>
      {isExpanded && <div className="mt-2 space-y-2">{children}</div>}
    </div>
  );
}

export function MedicalHistoryLog({
  historyLog,
  diagnoses,
  medications,
  procedures,
  mergeSuggestions,
  medDxLinks,
  onRefresh,
  onAddEntry,
  onEditEntry,
  onVerifyEntry,
  onDeleteEntry,
  onUpdateNotes,
  onStrikeOut,
  onUndoStrikeOut,
  onPageClick,
  onAcceptMerge,
  onRejectMerge,
  onBatchAcceptMerge,
  onBatchRejectMerge,
  onTriageComplete,
  onEditDiagnosis,
  onEditMedication,
  onEditProcedure,
}: MedicalHistoryLogProps) {
  const [activeTab, setActiveTab] = useState<TabType>('diagnoses');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Filter out merged duplicates for display
  const activeDiagnoses = diagnoses.filter(d => !d.is_merged_duplicate && !d.struck_out);
  const activeMedications = medications.filter(m => !m.is_merged_duplicate && !m.struck_out);
  const activeProcedures = procedures.filter(p => !p.is_merged_duplicate && !p.struck_out);

  // Struck-out entries (for separate section in card view)
  const struckOutDiagnoses = diagnoses.filter(d => !d.is_merged_duplicate && d.struck_out);
  const struckOutMedications = medications.filter(m => !m.is_merged_duplicate && m.struck_out);
  const struckOutProcedures = procedures.filter(p => !p.is_merged_duplicate && p.struck_out);

  const tabCounts: Record<TabType, number> = {
    diagnoses: activeDiagnoses.length,
    medications: activeMedications.length,
    procedures: activeProcedures.length,
  };

  // Group entries by status
  const groupedDiagnoses = DIAGNOSIS_SECTIONS.reduce(
    (acc, section) => {
      acc[section.status] = activeDiagnoses.filter(d => d.status === section.status);
      return acc;
    },
    {} as Record<DiagnosisStatus, HistoryDiagnosis[]>
  );

  const groupedMedications = MEDICATION_SECTIONS.reduce(
    (acc, section) => {
      acc[section.status] = activeMedications.filter(m => m.status === section.status);
      return acc;
    },
    {} as Record<MedicationStatus, HistoryMedication[]>
  );

  const groupedProcedures = PROCEDURE_SECTIONS.reduce(
    (acc, section) => {
      acc[section.status] = activeProcedures.filter(p => p.status === section.status);
      return acc;
    },
    {} as Record<ProcedureStatus, HistoryProcedure[]>
  );

  return (
    <div className="space-y-6">
      {/* Stats header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Medical History Log</h2>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-900/40 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors duration-150 ${
                  viewMode === 'grid'
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-700 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="Grid view"
              >
                <LayoutGrid size={14} />
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`p-1.5 rounded-md transition-colors duration-150 ${
                  viewMode === 'cards'
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-700 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="Card view"
              >
                <LayoutList size={14} />
              </button>
            </div>
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
              >
                <RefreshCw size={14} />
                Refresh
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 mb-1">
              <FileText size={14} />
              <span className="text-xs">Documents</span>
            </div>
            <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{historyLog.document_count}</p>
          </div>

          <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 mb-1">
              <Stethoscope size={14} />
              <span className="text-xs">Total Entries</span>
            </div>
            <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {activeDiagnoses.length + activeMedications.length + activeProcedures.length}
            </p>
          </div>

          {historyLog.pending_merge_count > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 mb-1">
                <GitMerge size={14} />
                <span className="text-xs">Pending Merges</span>
              </div>
              <p className="text-xl font-semibold text-blue-700 dark:text-blue-300">{historyLog.pending_merge_count}</p>
            </div>
          )}

          {historyLog.unresolved_discrepancy_count > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 mb-1">
                <AlertTriangle size={14} />
                <span className="text-xs">Discrepancies</span>
              </div>
              <p className="text-xl font-semibold text-amber-700 dark:text-amber-300">{historyLog.unresolved_discrepancy_count}</p>
            </div>
          )}

          {historyLog.unlinked_medication_count > 0 && (
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-purple-600 dark:text-purple-400 mb-1">
                <Link2 size={14} />
                <span className="text-xs">Unlinked Meds</span>
              </div>
              <p className="text-xl font-semibold text-purple-700 dark:text-purple-300">{historyLog.unlinked_medication_count}</p>
            </div>
          )}
        </div>
      </div>

      {/* Merge suggestions banner */}
      {mergeSuggestions && onAcceptMerge && onRejectMerge && onBatchAcceptMerge && onBatchRejectMerge && (
        <MergeSuggestionBanner
          suggestions={mergeSuggestions}
          onAccept={onAcceptMerge}
          onReject={onRejectMerge}
          onBatchAccept={onBatchAcceptMerge}
          onBatchReject={onBatchRejectMerge}
          onTriageComplete={onTriageComplete}
        />
      )}

      {/* Grid view */}
      {viewMode === 'grid' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <CompactHistoryGrid
            diagnoses={diagnoses}
            medications={medications}
            procedures={procedures}
            medDxLinks={medDxLinks || []}
            onEditDiagnosis={onEditDiagnosis}
            onEditMedication={onEditMedication}
            onEditProcedure={onEditProcedure}
            onVerifyEntry={onVerifyEntry}
            onDeleteEntry={onDeleteEntry}
            onAddEntry={onAddEntry}
            onStrikeOut={onStrikeOut}
            onUndoStrikeOut={onUndoStrikeOut}
            onPageClick={onPageClick}
          />
        </div>
      )}

      {/* Cards view (original tabbed layout) */}
      {viewMode === 'cards' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Tab header */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-150 ${
                    isActive
                      ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      isActive ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {tabCounts[tab.id]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="p-4">
            {activeTab === 'diagnoses' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary-100 dark:bg-primary-900/40 rounded">
                      <Stethoscope size={16} className="text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">Diagnoses</h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({activeDiagnoses.length})</span>
                  </div>
                  {onAddEntry && (
                    <button
                      onClick={() => onAddEntry('diagnosis')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
                    >
                      <Plus size={14} />
                      Add
                    </button>
                  )}
                </div>
                {activeDiagnoses.length === 0 ? (
                  <div className="text-center py-8">
                    <Stethoscope size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No diagnoses found</p>
                    {onAddEntry && (
                      <button
                        onClick={() => onAddEntry('diagnosis')}
                        className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        Add a diagnosis manually
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {DIAGNOSIS_SECTIONS.map(section => (
                      <CollapsibleSection
                        key={section.status}
                        title={section.label}
                        count={groupedDiagnoses[section.status]?.length || 0}
                        color={section.color}
                        bgColor={section.bgColor}
                        defaultExpanded={section.status === 'active' || section.status === 'uncertain'}
                      >
                        {(groupedDiagnoses[section.status] || []).map(diagnosis => (
                          <HistoryEntryCard
                            key={diagnosis.id}
                            entry={diagnosis}
                            entryType="diagnosis"
                            onEdit={onEditEntry ? e => onEditEntry(e, 'diagnosis') : undefined}
                            onVerify={onVerifyEntry ? e => onVerifyEntry(e, 'diagnosis') : undefined}
                            onDelete={onDeleteEntry ? e => onDeleteEntry(e, 'diagnosis') : undefined}
                            onUpdateNotes={onUpdateNotes ? (e, notes) => onUpdateNotes(e, 'diagnosis', notes) : undefined}
                            onStrikeOut={onStrikeOut ? e => onStrikeOut(e, 'diagnosis') : undefined}
                            onUndoStrikeOut={onUndoStrikeOut ? e => onUndoStrikeOut(e, 'diagnosis') : undefined}
                            onPageClick={onPageClick}
                          />
                        ))}
                      </CollapsibleSection>
                    ))}
                    {struckOutDiagnoses.length > 0 && (
                      <CollapsibleSection
                        title="Struck Out"
                        count={struckOutDiagnoses.length}
                        color="text-gray-500 dark:text-gray-400"
                        bgColor="bg-gray-100 dark:bg-gray-700"
                        defaultExpanded={false}
                      >
                        {struckOutDiagnoses.map(diagnosis => (
                          <HistoryEntryCard
                            key={diagnosis.id}
                            entry={diagnosis}
                            entryType="diagnosis"
                            onDelete={onDeleteEntry ? e => onDeleteEntry(e, 'diagnosis') : undefined}
                            onUndoStrikeOut={onUndoStrikeOut ? e => onUndoStrikeOut(e, 'diagnosis') : undefined}
                            onPageClick={onPageClick}
                          />
                        ))}
                      </CollapsibleSection>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'medications' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary-100 dark:bg-primary-900/40 rounded">
                      <Pill size={16} className="text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">Medications</h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({activeMedications.length})</span>
                  </div>
                  {onAddEntry && (
                    <button
                      onClick={() => onAddEntry('medication')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
                    >
                      <Plus size={14} />
                      Add
                    </button>
                  )}
                </div>
                {activeMedications.length === 0 ? (
                  <div className="text-center py-8">
                    <Pill size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No medications found</p>
                    {onAddEntry && (
                      <button
                        onClick={() => onAddEntry('medication')}
                        className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        Add a medication manually
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {MEDICATION_SECTIONS.map(section => (
                      <CollapsibleSection
                        key={section.status}
                        title={section.label}
                        count={groupedMedications[section.status]?.length || 0}
                        color={section.color}
                        bgColor={section.bgColor}
                        defaultExpanded={section.status === 'current' || section.status === 'uncertain'}
                      >
                        {(groupedMedications[section.status] || []).map(medication => (
                          <HistoryEntryCard
                            key={medication.id}
                            entry={medication}
                            entryType="medication"
                            onEdit={onEditEntry ? e => onEditEntry(e, 'medication') : undefined}
                            onVerify={onVerifyEntry ? e => onVerifyEntry(e, 'medication') : undefined}
                            onDelete={onDeleteEntry ? e => onDeleteEntry(e, 'medication') : undefined}
                            onUpdateNotes={onUpdateNotes ? (e, notes) => onUpdateNotes(e, 'medication', notes) : undefined}
                            onStrikeOut={onStrikeOut ? e => onStrikeOut(e, 'medication') : undefined}
                            onUndoStrikeOut={onUndoStrikeOut ? e => onUndoStrikeOut(e, 'medication') : undefined}
                            onPageClick={onPageClick}
                          />
                        ))}
                      </CollapsibleSection>
                    ))}
                    {struckOutMedications.length > 0 && (
                      <CollapsibleSection
                        title="Struck Out"
                        count={struckOutMedications.length}
                        color="text-gray-500 dark:text-gray-400"
                        bgColor="bg-gray-100 dark:bg-gray-700"
                        defaultExpanded={false}
                      >
                        {struckOutMedications.map(medication => (
                          <HistoryEntryCard
                            key={medication.id}
                            entry={medication}
                            entryType="medication"
                            onDelete={onDeleteEntry ? e => onDeleteEntry(e, 'medication') : undefined}
                            onUndoStrikeOut={onUndoStrikeOut ? e => onUndoStrikeOut(e, 'medication') : undefined}
                            onPageClick={onPageClick}
                          />
                        ))}
                      </CollapsibleSection>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'procedures' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary-100 dark:bg-primary-900/40 rounded">
                      <Syringe size={16} className="text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">Procedures</h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({activeProcedures.length})</span>
                  </div>
                  {onAddEntry && (
                    <button
                      onClick={() => onAddEntry('procedure')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors min-h-[44px] sm:min-h-0"
                    >
                      <Plus size={14} />
                      Add
                    </button>
                  )}
                </div>
                {activeProcedures.length === 0 ? (
                  <div className="text-center py-8">
                    <Syringe size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No procedures found</p>
                    {onAddEntry && (
                      <button
                        onClick={() => onAddEntry('procedure')}
                        className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        Add a procedure manually
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {PROCEDURE_SECTIONS.map(section => (
                      <CollapsibleSection
                        key={section.status}
                        title={section.label}
                        count={groupedProcedures[section.status]?.length || 0}
                        color={section.color}
                        bgColor={section.bgColor}
                        defaultExpanded={section.status === 'completed' || section.status === 'uncertain'}
                      >
                        {(groupedProcedures[section.status] || []).map(procedure => (
                          <HistoryEntryCard
                            key={procedure.id}
                            entry={procedure}
                            entryType="procedure"
                            onEdit={onEditEntry ? e => onEditEntry(e, 'procedure') : undefined}
                            onVerify={onVerifyEntry ? e => onVerifyEntry(e, 'procedure') : undefined}
                            onDelete={onDeleteEntry ? e => onDeleteEntry(e, 'procedure') : undefined}
                            onUpdateNotes={onUpdateNotes ? (e, notes) => onUpdateNotes(e, 'procedure', notes) : undefined}
                            onStrikeOut={onStrikeOut ? e => onStrikeOut(e, 'procedure') : undefined}
                            onUndoStrikeOut={onUndoStrikeOut ? e => onUndoStrikeOut(e, 'procedure') : undefined}
                            onPageClick={onPageClick}
                          />
                        ))}
                      </CollapsibleSection>
                    ))}
                    {struckOutProcedures.length > 0 && (
                      <CollapsibleSection
                        title="Struck Out"
                        count={struckOutProcedures.length}
                        color="text-gray-500 dark:text-gray-400"
                        bgColor="bg-gray-100 dark:bg-gray-700"
                        defaultExpanded={false}
                      >
                        {struckOutProcedures.map(procedure => (
                          <HistoryEntryCard
                            key={procedure.id}
                            entry={procedure}
                            entryType="procedure"
                            onDelete={onDeleteEntry ? e => onDeleteEntry(e, 'procedure') : undefined}
                            onUndoStrikeOut={onUndoStrikeOut ? e => onUndoStrikeOut(e, 'procedure') : undefined}
                            onPageClick={onPageClick}
                          />
                        ))}
                      </CollapsibleSection>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
